/**
 * SelectedListingCard - Preview card when marker is clicked
 *
 * Diamond Architecture: Matches Rails PopupTemplateComponent and Mobile SelectedListingCard
 * - Shows when a map marker is clicked
 * - Positioned at bottom of screen (mobile) or as popup (desktop)
 * - Has close button, save button, image carousel
 * - Click navigates to listing detail
 *
 * Unicorn Standard: Entity Component (src/components/listings)
 * Used by: ListingsClient when selectedId is set from marker click
 *
 * ★ THE ROOT IS `CardSurface` BECAUSE THE ARROWS DEPEND ON IT (§2.13). This is
 * the THIRD host of `SlideCarousel`, and the one that proves why the card root
 * had to become a primitive. #6839 made the arrows' reveal `group-hover:`-only
 * — correct for the two hosts whose roots happened to carry `group`, and
 * silently fatal here: this card's root was hand-rolled, so from that merge its
 * arrows were reachable by Tab and by nothing else, on the desktop map popover
 * AND on the mobile marker preview. No gate could see it; an invisible arrow
 * renders, and the popover had no test at all. The root now comes from the seam
 * that mints the reveal rule, so the pairing is one fact and not two.
 */
import { Link } from '@/shared/i18n/routing';
import { useLocale, useTranslations } from '@/shared/i18n/useTranslations';
import { getListingHref } from '@/shared/lib/listings/url';
import { formatPrice } from '@letsgethome/lib/currency';
import type { TransactionModeValue } from '@letsgethome/lib/types/listings';
import { plainText, plaintextString } from '@letsgethome/types/branded';
import { CardControlLayer, CardSurface } from '@letsgethome/ui/card-layers';
import { cn } from '@letsgethome/ui/lib/utils';
import { UnstyledButton } from '@letsgethome/ui/unstyled-button';
import { X } from 'lucide-react';

import { CardCarousel } from './CardCarousel';
import { getBasicInfoParts, getDisplayTitle, getListingPriceData } from './listing-utils';
import type { ListingCardData } from './ListingCard.types';
import { SaveButton } from './SaveButton';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

interface SelectedListingCardProps {
  /** The selected listing to display */
  listing: ListingCardData;
  /** Transaction type for price display */
  transactionType?: TransactionModeValue;
  /** Callback to dismiss/close the card */
  onDismiss: () => void;
  /** Additional classes */
  className?: string;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export function SelectedListingCard({
  listing,
  transactionType = 'rent',
  onDismiss,
  className,
}: SelectedListingCardProps) {
  const t = useTranslations('listings.card');
  const tA11y = useTranslations('common');
  const locale = useLocale();

  // STRICT intent-scoped price (Rust SSOT, matches ListingCard): null = not
  // offered for this intent or unpriced — an upstream bug. Hide the price
  // element; NEVER substitute the other mode's price.
  const { price } = getListingPriceData(listing, transactionType);
  const formattedPrice = price === null ? null : formatPrice(price, locale);

  // Rust SSOT: never null/empty — falls back to the localized "Untitled Listing".
  const displayTitle = getDisplayTitle(listing, locale);
  const { bedrooms, bathrooms, area, isStudio } = getBasicInfoParts(listing);

  // The second line is the MAPPER's decision, exactly as on `ListingCard` (D9).
  const location = listing.locationLine ?? null;

  // Format basic info with translations
  const basicInfoParts: string[] = [];
  if (isStudio) {
    basicInfoParts.push(t('bedroomsStudio'));
  } else if (bedrooms !== null && bedrooms > 0) {
    basicInfoParts.push(t('bedrooms', { count: bedrooms }));
  }
  if (bathrooms !== null && bathrooms > 0) {
    basicInfoParts.push(t('bathrooms', { count: bathrooms }));
  }
  if (area) {
    basicInfoParts.push(t('sqft', { area }));
  }
  const basicInfo = basicInfoParts.join(' · ');

  return (
    <CardSurface
      className={cn('bg-background rounded-xl shadow-lg overflow-hidden w-full md:w-80', className)}
    >
      {/* Image section */}
      <div className="relative aspect-[16/9] md:aspect-[3/2] bg-muted">
        <CardCarousel
          images={listing.imageKeys}
          alt={plainText(displayTitle)}
          fallback={<span className="text-muted-foreground">{t('noImage')}</span>}
          sizes="(max-width: 768px) 100vw, 320px"
        />

        {/* Action buttons overlay — on the card seam's controls layer (LET-85).
            This popup has no stretched link over its image, so the layer
            costs nothing here; it is the same wrapper the other card
            surfaces use, which is what keeps `no-raw-z-layer` clean. */}
        <CardControlLayer className="top-3 right-3 flex items-center gap-2">
          {/* Save button */}
          <SaveButton saveable={{ kind: 'listing', id: listing.id }} serverSaved={listing.saved} />

          {/* Close button */}
          <UnstyledButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDismiss();
            }}
            className={cn(
              'flex items-center justify-center rounded-full',
              'bg-background/90 backdrop-blur-sm shadow-md',
              'h-8 w-8 transition hover:scale-105 active:scale-95'
            )}
            aria-label={tA11y('a11y.close')}
          >
            <X className="h-4 w-4" />
          </UnstyledButton>
        </CardControlLayer>
      </div>

      {/* Content section */}
      <Link
        href={getListingHref(listing)}
        prefetch="intent"
        className="block p-3 hover:bg-muted/50 transition-colors"
      >
        {/* Title */}
        <p className="font-medium text-foreground line-clamp-1">{plaintextString(displayTitle)}</p>

        {/* Subtitle / Location */}
        {location && location !== displayTitle && (
          <p className="text-sm text-muted-foreground line-clamp-1">{plaintextString(location)}</p>
        )}

        {/* Basic info + Price row */}
        <div className="flex items-baseline justify-between gap-2 mt-1">
          {basicInfo && (
            <p className="text-sm text-muted-foreground truncate">{plaintextString(basicInfo)}</p>
          )}
          {formattedPrice && (
            <p className="text-sm font-medium text-foreground whitespace-nowrap">
              {formattedPrice}
            </p>
          )}
        </div>
      </Link>
    </CardSurface>
  );
}
